// 📄 components/ui/PillMultiSelect.tsx
//
// Selector múltiple de texto libre con chips — para filtros tipo
// "smVendorIds: string[]" (Buscar Proveedores, SLP) donde no hay un
// catálogo fijo de valores posibles. A diferencia de CatalogoMultiSelect.tsx
// (que resuelve SIEMPRE contra un id real de otro catálogo, sin texto
// libre), acá el usuario escribe cualquier valor y lo agrega con Enter/
// coma — más `sugerencias` opcionales (valores comunes conocidos, ej.
// "Qualified"/"Registered") que se muestran como píldoras de un click,
// para no obligar a escribir a mano lo más frecuente. Ninguno de los dos
// componentes reemplaza al otro — cubren necesidades distintas.
import { useState, KeyboardEvent } from 'react';
import { X, Plus } from 'lucide-react';

interface PillMultiSelectProps {
  label: string;
  valores: string[];
  onChange: (valores: string[]) => void;
  // Valores comunes que se muestran como píldoras de acceso rápido (click
  // para agregar/quitar) — opcional, si no viene solo hay texto libre.
  sugerencias?: string[];
  placeholder?: string;
}

export default function PillMultiSelect({ label, valores, onChange, sugerencias, placeholder }: PillMultiSelectProps) {
  const [texto, setTexto] = useState('');

  const agregar = (valor: string) => {
    const limpio = valor.trim();
    if (!limpio || valores.includes(limpio)) return;
    onChange([...valores, limpio]);
  };

  const quitar = (valor: string) => onChange(valores.filter((v) => v !== valor));

  const toggleSugerencia = (valor: string) => {
    if (valores.includes(valor)) quitar(valor);
    else agregar(valor);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      agregar(texto);
      setTexto('');
    } else if (e.key === 'Backspace' && !texto && valores.length > 0) {
      quitar(valores[valores.length - 1]);
    }
  };

  // Sugerencias no seleccionadas todavía (las ya elegidas se ven como chip
  // normal más abajo, junto con cualquier valor libre que se haya tipeado).
  const sugerenciasDisponibles = (sugerencias || []).filter((s) => !valores.includes(s));

  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>

      {sugerenciasDisponibles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {sugerenciasDisponibles.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleSugerencia(s)}
              className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border border-white/10 text-gray-400 hover:text-white hover:border-indigo-500/40 hover:bg-indigo-500/10 transition-all"
            >
              <Plus className="w-3 h-3" /> {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 bg-zinc-800 border border-white/10 rounded-lg p-2 focus-within:border-indigo-500">
        {valores.map((v) => (
          <span key={v} className="flex items-center gap-1 text-xs font-medium bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 rounded-full pl-2.5 pr-1.5 py-1">
            {v}
            <button type="button" onClick={() => quitar(v)} className="hover:text-white transition-colors">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (texto.trim()) { agregar(texto); setTexto(''); } }}
          placeholder={valores.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] bg-transparent text-sm text-white placeholder:text-gray-500 focus:outline-none py-0.5"
        />
      </div>
    </div>
  );
}
